import { type JSX, Show, splitProps } from 'solid-js'
import { cn } from '../lib/cn.ts'
import { tv } from '../lib/tv.ts'

const field = tv({
  slots: {
    /*
     * `content-start` matters more than it looks. Side by side in a two-column form grid, every
     * Field is stretched to the tallest one — and without this the internal rows stretch too, so
     * a Field with no hint grows its LABEL row and its control sits lower than its neighbour's.
     * Pinning the rows to their natural height makes controls line up across a row and lets the
     * slack collect at the bottom, where nothing can see it.
     */
    root: 'grid content-start gap-field-gap text-body',
    label: 'font-medium text-text',
    hint: 'text-caption text-text-subtle',
    error: 'text-caption text-status-critical',
  },
})

export interface FieldProps extends Omit<JSX.HTMLAttributes<HTMLDivElement>, 'children'> {
  for: string
  label: string
  hint?: string
  error?: string
  children: JSX.Element
}

export const Field = (props: FieldProps) => {
  const [local, rest] = splitProps(props, ['for', 'label', 'hint', 'error', 'children', 'class'])
  const styles = field()

  return (
    <div {...rest} data-field-error={Boolean(local.error)} class={cn(styles.root(), local.class)}>
      <label class={styles.label()} for={local.for}>
        {local.label}
      </label>
      {local.children}
      <Show when={local.hint}>
        {(hint) => (
          <span id={`${local.for}-hint`} class={styles.hint()}>
            {hint()}
          </span>
        )}
      </Show>
      <Show when={local.error}>
        {(error) => (
          <span id={`${local.for}-error`} role="alert" class={styles.error()}>
            {error()}
          </span>
        )}
      </Show>
    </div>
  )
}
